import React, { useState } from 'react';
import SendMessageButton from '../components/SendMessageButton';
import SocialMediaIcons from '../components/SocialMediaIcons';
import './Contact.css';
import featureFlags from '../configuration/featureFlags';
import { Helmet } from 'react-helmet-async';
import siteConfig from '../configuration/siteConfiguration';

const Contact = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [messageSent, setMessageSent] = useState(false);

  const validateEmail = (email) => {
    // Basic email validation regex
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailTouched(true);
    if (emailTouched) { // Validate on change only after first blur
      if (validateEmail(e.target.value)) {
        setEmailError(false);
        setEmailSuccess(true);
      } else {
        setEmailError(true);
        setEmailSuccess(false);
      }
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    if (email.length > 0 && !validateEmail(email)) {
      setEmailError(true);
      setEmailSuccess(false);
    } else if (email.length > 0 && validateEmail(email)) {
      setEmailError(false);
      setEmailSuccess(true);
    } else {
      setEmailError(false);
      setEmailSuccess(false);
    }
  };

  const handleSendMessage = async () => {
    if (!validateEmail(email)) {
      setEmailError(true);
      setEmailSuccess(false);
      alert('Please enter a valid email address.');
      return;
    }

    try {
      const response = await fetch('https://formspree.io/f/xvgdydoq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          subject,
          companyName,
          message,
        }),
      });
      if (response.ok) {
        setMessageSent(true);
        setFirstName('');
        setLastName('');
        setEmail('');
        setSubject('');
        setCompanyName('');
        setMessage('');
        setEmailError(false);
        setEmailSuccess(false);
        setEmailTouched(false);
      } else {
        const errorText = await response.text();
        alert('Failed to send message: ' + errorText);
      }
    } catch (error) {
      alert('Error sending message: ' + error.message);
    }
  };


  const addressLink1 = "https://www.google.com/maps/search/?api=1&query=271,+6th+Main+Road,+HAL+2nd+Stage,+Motappapalya,+Indira+Nagar,+Bengaluru,+Karnataka,+India,+560038";
  const addressLink2 = "https://www.google.com/maps/place/462,+11th+B+Cross+Rd,+1st+Phase,+J.+P.+Nagar,+Bengaluru,+Karnataka+560078/@12.9083722,77.5830018,17z/data=!3m1!4b1!4m6!3m5!1s0x3bae156d16ccb5fb:0x978782a17ca95d89!8m2!3d12.9083722!4d77.5830018!16s%2Fg%2F11csj3rt9v?entry=ttu&g_ep=EgoyMDI1MDYyMy4yIKXMDSoASAFQAw%3D%3D";
  // Validation for all fields
  const isFormValid =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    subject.trim() &&
    companyName.trim() &&
    message.trim() &&
    validateEmail(email);

  return (
    <>
      <Helmet>
        <title>Contact Us - Cognitva</title>
        <meta name="description" content="Get in touch with Cognitva for inquiries, support, or feedback. We are here to assist you with your enterprise-wide intelligence needs." />
        <link rel="canonical" href="/contact" />
        <meta property="og:title" content="Contact Us - Cognitva" />
        <meta property="og:description" content="Get in touch with Cognitva for inquiries, support, or feedback. We are here to assist you with your enterprise-wide intelligence needs." />
        <meta property="og:image" content="/companylogo.svg" />
        <meta property="og:url" content="/contact" />
      </Helmet>
      <div className="contact-page">
        <div className="contact-header-section">
          <p className="drop-us-a-line">DROP US A LINE</p>
          <h1 className="contact-title">We'd love to hear from you</h1>
        </div>

        <div className="contact-content-container">
          <div className="contact-info-section">
            <div className="info-item">
              <div className="icon-wrapper">
                <img src="/images/emailicon.svg" alt="Email icon" className="contact-icon" />
              </div>
              <div className="text-content">
                <p className="label">EMAIL US</p>
                <a href="mailto:info@cognitva.ai" className="value-link">info@cognitva.ai</a>
              </div>
            </div>

            <div className="info-item">
              <div className="icon-wrapper">
                <img src="/images/phoneicon.svg" alt="Phone icon" className="contact-icon" />
              </div>
              <div className="text-content">
                <p className="label">PHONE NUMBER</p>
                <a href="tel:+919880140996" className="value-link">+91-9880140996</a>
              </div>
            </div>

            <div className="info-item">
              <div className="icon-wrapper">
                <a
                  href={addressLink1}
                  target="_blank" className="value-link address-link">
                  <img src="/images/addressicon.svg" alt="Address icon" className="contact-icon" /></a>
              </div>
              <div className="text-content">
                <p className="label">OFFICE ADDRESS</p>
                <a
                  href={addressLink1}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="value-link address-link"
                >
                  271, 6th Main Road, HAL 2nd Stage,<br />
                  Motappapalya, Indira Nagar,<br />
                  Bengaluru, Karnataka, India<br />
                  560038
                </a>

              </div>
            </div>
            <div className="info-item">
              <div className="icon-wrapper">
                <a
                  href={addressLink2}
                  target="_blank" className="value-link address-link"><img src="/images/addressicon.svg" alt="Address icon" className="contact-icon" /></a>
              </div>
              <div className="text-content">
                <p className="label">REGISTERED ADDRESS</p>
                <a
                  href={addressLink2}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="value-link address-link"
                >
                  Samrudhi, St. Marys School Road Nr. ESI Dispensary,<br />
                  Gandhi Nagar, Gandinagar Tumkur, Tumkur, Tumkur,<br />
                  Karnataka, India<br />
                  572102
                </a>

              </div>
            </div>

            {featureFlags.contactUs.showSocialMediaLinks && <div className="social-media-section">
              <div className="social-media-header">
                <img src="/images/line2.svg" alt="line" className="social-media-line" />
                <p className="connect-with-us">Connect with us:</p>
              </div>
              <SocialMediaIcons className="light" />
            </div>}
          </div>

          <div className="contact-form-section">
            <div className="form-group">
              <label className="form-label">Name</label>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="First name"
                  className="form-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Last name"
                  className="form-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                placeholder="Email address"
                className={`form-input full-width ${emailError ? 'error' : ''} ${emailSuccess ? 'success' : ''}`}
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
              />
              {emailError && <p className="error-message">Invalid email address</p>}
            </div>

            <div className="form-group">
              <label className="form-label">Subject</label>
              <input
                type="text"
                placeholder="Title for the message"
                className="form-input full-width"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Company name</label>
              <input
                type="text"
                placeholder="Brand / Company / Product"
                className="form-input full-width"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea
                placeholder="Tell us what you want not less than 200 characters"
                className="form-textarea full-width"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              ></textarea>
            </div>

            {/* Button or Success Message */}
            {messageSent ? (
              <div className="success-message-bar">
                <img src="/images/success.svg" alt="Success" className="success-icon-bar" />
                <span className="success-text-bar">Successfully received your message!</span>
              </div>
            ) : (
              <SendMessageButton onClick={handleSendMessage} disabled={!isFormValid}>
                Send Message
              </SendMessageButton>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;